import { Injectable } from '@angular/core';
import { AnalyticsService } from '@wdpr/angular-analytics';
import {DateHandler} from './date-handler';
import {Constants} from '../constants';
import {vsprintf} from 'sprintf-js';
import {ConfigService} from '../services/config.service';
import entries from 'lodash-es/entries';

@Injectable()
export class AnalyticsHandler {
    /**
     * App configuration object
     * @var {any}
     */
    private _config: any;

    /**
     * Current selected date
     * @var {string}
     */
    private _selectedDate: string;

    /**
     * Current selected segment
     * @var {string}
     */
    private _selectedSegment: string;

    /**
     * Current Analytics Link ID
     * @var {string}
     */
    private _linkId: string;

    /**
     * Parks availability array
     * @var {Array<any>}
     */
    private _parksAvailability: Array<any>;

    /**
     * Analytics model
     * @var {any}
     */
    private _model: any;

    /**
     * Today's date
     * @var {string}
     */
    private readonly _today: string;

    /**
     * Class constructor
     *
     * @param _analyticsService
     * @param _dateHandler
     * @param _configService
     */
    constructor(
        private _analyticsService: AnalyticsService,
        private _dateHandler: DateHandler,
        private _configService: ConfigService
    ) {
        this._config = this._configService.getConfig();
        this._today = this._dateHandler.formatDate(Constants.DATE_RAW);
    }

    /**
     * Sets the current link ID
     *
     * @param linkId
     * @returns {AnalyticsHandler}
     */
    public setLinkId(linkId: string): AnalyticsHandler {
        this._linkId = linkId.trim();

        return this;
    }

    /**
     * Sets the current selected date
     *
     * @param selectedDate
     * @returns {AnalyticsHandler}
     */
    public setSelectedDate(selectedDate: string): AnalyticsHandler {
        this._selectedDate = selectedDate;

        return this;
    }

    /**
     * Sets the current selected segment
     *
     * @param selectedSegment
     * @returns {AnalyticsHandler}
     */
    public setSelectedSegment(selectedSegment: string): AnalyticsHandler {
        this._selectedSegment = selectedSegment;

        return this;
    }

    /**
     * Sets the park availability object
     *
     * @param parksAvailability
     * @returns {AnalyticsHandler}
     */
    public setParksAvailability(parksAvailability: Array<any>): AnalyticsHandler {
        this._parksAvailability = parksAvailability;

        return this;
    }

    /**
     * Updates the current model for page view event
     *
     * @returns {void}
     */
    public trackPageViewEvent(): void {
        this._analyticsService.getModel().then(
            (model: any) => {
                model.pageVariant = this._getPageVariant();
                model.store = this._getStoreId();
                model.itemAvailability = this._setAvailabilityDataForCurrentMonth();

                this._analyticsService.update();
            }
        );
    }

    /**
     * Generates the model and posts it to analytics endpoint
     *
     * @param event
     * @returns {void}
     */
    public trackAnalytics(event: string): void {
        this._model = {
            trackingType: Constants.ANALYTICS_TRACKING_TYPE,
            pageVariant: this._getPageVariant(),
            store: this._getStoreId()
        };

        switch (event) {
            case Constants.ANALYTICS_EVENT_TYPE_DATE_CHANGED:
                this._setModelDataForDateChange();
                break;
            case Constants.ANALYTICS_EVENT_TYPE_MONTH_NEXT:
            case Constants.ANALYTICS_EVENT_TYPE_MONTH_PREVIOUS:
                this._setModelDataForMonthChange();
                break;
            case Constants.ANALYTICS_EVENT_TYPE_SEGMENT_CHANGED:
                this._setModelDataForSegmentChange();
                break;
        }

        this._analyticsService.trackElement(this._model);
    }

    /**
     * Gets the page variant for analytics model
     *
     * @private
     * @returns {string}
     */
    private _getPageVariant(): string {
        return Constants[`ANALYTICS_SEGMENT_${this._selectedSegment.toUpperCase()}`];
    }

    /**
     * Gets the store ID (defaulting to Consumer for now since we don't have that data yet)
     *
     * @private
     * @returns {string}
     */
    private _getStoreId(): string {
        return `${this._config.brand}Consumer`;
    }

    /**
     * Generates the analytics link ID
     *
     * @param replacements
     * @private
     * @returns {void}
     */
    private _generateLinkId(replacements?: Array<string>): void {
        replacements = replacements || [];

        replacements.unshift(this._config.brand);

        this._linkId = vsprintf(this._linkId, replacements);
    }

    /**
     * Returns the selected date in the format required by analytics
     *
     * @private
     * @returns {string}
     */
    private _getDateInAnalyticsFormat(): string {
        return this._dateHandler.formatDate(
            Constants.DATE_ANALYTICS,
            this._dateHandler.convertDateToMomentObject(this._selectedDate)
        );
    }

    /**
     * Gets the difference between the selected date and today
     *
     * @private
     * @returns {number}
     */
    private _getSelectedDateDiff(): number {
        return this._dateHandler.getDateDiff(
            this._selectedDate,
            this._today,
            Constants.DATE_UNIT_DAYS
        );
    }

    /**
     * Sets the model data to track date change events
     *
     * @private
     * @returns {void}
     */
    private _setModelDataForDateChange(): void {
        const intervals: Array<any> = [];

        for (const park of this._parksAvailability) {
            intervals.push({
                name: park.parkAnalyticsName,
                value: park.available ? 1 : 0
            });
        }

        this._generateLinkId();

        this._model = {
            ...this._model,
            ...{
                linkId: this._linkId,
                bookingSearchArrivalDate: this._getDateInAnalyticsFormat(),
                bookingSearchDaysBetweenSearchAndArrival: this._getSelectedDateDiff(),
                itemAvailability: {
                    id: Constants.ANALYTICS_ITEM_PARK_AVAILABILITY_ID,
                    intervals: intervals
                }
            }
        };
    }

    /**
     * Sets the model data to track month change events
     *
     * @private
     * @returns {void}
     */
    private _setModelDataForMonthChange(): void {
        this._generateLinkId();

        this._model = {
            ...this._model,
            ...{
                linkId: this._linkId,
                itemAvailability: this._setAvailabilityDataForCurrentMonth()
            }
        };
    }

    /**
     * Sets the availability information for the current month
     *
     * @private
     * @returns {object}
     */
    private _setAvailabilityDataForCurrentMonth(): object {
        if (!this._parksAvailability.length) {
	     return null;
	}

        const filteredDates = {
                'full': this._getDatesByAvailability(Constants.AVAILABILITY_FULL).length,
                'partial': this._getDatesByAvailability(Constants.AVAILABILITY_PARTIAL).length,
                'none': this._getDatesByAvailability(Constants.AVAILABILITY_NONE).length
            };
        const monthInfo = this._dateHandler.formatDate(
                Constants.DATE_MONTH_AVAIL_ANALYTICS,
                this._dateHandler.convertDateToMomentObject(this._parksAvailability[0].date)
            );
         const totalAvailableDays = filteredDates.full + filteredDates.partial;
         const  intervals = [];

        for (const [availabilityType, amount] of entries(filteredDates)) {
            intervals.push({
                name: vsprintf(Constants[`ANALYTICS_MONTH_AVAIL_${availabilityType.toUpperCase()}`], [monthInfo]),
                value: amount
            });
        }

        return {
            id: Constants.ANALYTICS_ITEM_MONTH_AVAILABILITY_ID,
            total: totalAvailableDays,
            intervals: intervals
        };
    }

    /**
     * Sets the model data for segment change events
     *
     * @private
     * @returns {void}
     */
    private _setModelDataForSegmentChange(): void {
        this._generateLinkId([Constants[`ANALYTICS_SEGMENT_${this._selectedSegment.toUpperCase()}`]]);

        this._model = {
            ...this._model,
            ...{
                linkId: this._linkId,
                events: Constants.ANALYTICS_CHANGE_VARIANT
            }
        };
    }

    /**
     * Filters the month dates based on their availability type
     *
     * @param availabilityType
     * @private
     * @returns {Array<any>}
     */
    private _getDatesByAvailability(availabilityType: string): Array<any> {
        return this._parksAvailability.filter((date) => {
            return date.availability === availabilityType;
        });
    }
}
